import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { CustomerService } from '../../services/customer.service';
import { CustomerCreateDTO } from '../../interfaces/sales/customer.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [taxRegistrationNo, setTaxRegistrationNo] = useState('');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const customer = await CustomerService.getById(id!);
        setName(customer.name);
        setContactPerson(customer.contactPerson ?? '');
        setEmail(customer.email ?? '');
        setPhone(customer.phone ?? '');
        setAddress(customer.address ?? '');
        setTaxRegistrationNo(customer.taxRegistrationNo ?? '');
      } catch (e) {
        toast.showApiError(e, 'Failed to load customer');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id]);

  const handleSubmit = async () => {
    if (name.trim() === '') return;
    const institutionId = AuthService.getSelectedInstitution()!;
    const dto: CustomerCreateDTO = {
      name: name.trim(),
      contactPerson: contactPerson.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      address: address.trim() || undefined,
      taxRegistrationNo: taxRegistrationNo.trim() || undefined,
      institutionId,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await CustomerService.update(id!, dto);
        toast.showSuccess('Customer updated');
        navigate(`/sales/customers/${id}`);
      } else {
        const created = await CustomerService.create(dto);
        toast.showSuccess('Customer created');
        navigate(`/sales/customers/${created.id}`);
      }
    } catch (e) {
      toast.showApiError(e, isEdit ? 'Failed to update customer' : 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? 'Edit Customer' : 'New Customer'}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
          <TextField
            label="Contact Person"
            value={contactPerson}
            onChange={e => setContactPerson(e.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
          <TextField
            label="Address"
            value={address}
            onChange={e => setAddress(e.target.value)}
            multiline
            rows={3}
          />
          <TextField
            label="Tax Registration No."
            value={taxRegistrationNo}
            onChange={e => setTaxRegistrationNo(e.target.value)}
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || name.trim() === ''}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Customer'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerForm;
